import { useEffect, useRef, useState, useCallback } from "react";
import Message from "./Message";
import MessageInput from "./MessageInput";
import VoiceCallButton from "./VoiceCallButton.jsx";
import AnnouncementView from "./AnnouncementView.jsx";
import ForumView from "./ForumView.jsx";
import StageView from "./StageView.jsx";
import { MessageSkeleton } from "./Skeletons.jsx";
import { useMessageStore } from "../../stores/messageStore.js";
import { useChannelStore } from "../../stores/channelStore.js";
import { usePresenceStore } from "../../stores/presenceStore.js";
import { useModalStore } from "../../stores/modalStore.js";
import { useServerStore } from "../../stores/serverStore.js";

export default function ChatArea({ onOpenSettings, onToggleActivity, onMobileOpenChannels, onMobileOpenMembers }) {
  const messagesRef  = useRef(null);
  const searchInputRef = useRef(null);
  const { onOpen }   = useModalStore();

  const currentChannelId  = useChannelStore(s => s.currentChannelId);
  const getCurrentChannel = useChannelStore(s => s.getCurrentChannel);
  const messages          = useMessageStore(s => s.messages);
  const isLoading         = useMessageStore(s => s.isLoading);
  const getChannelTypingUsers = usePresenceStore(s => s.getChannelTypingUsers);
  const currentServerId   = useServerStore(s => s.currentServerId);

  const currentChannel   = getCurrentChannel();
  const channelMessages  = messages.filter(m => m.channelId === currentChannelId);
  const typingUsers      = getChannelTypingUsers(currentChannelId);

  const [replyTo,        setReplyTo]        = useState(null);
  const [showScrollBtn,  setShowScrollBtn]  = useState(false);
  const isAtBottom = useRef(true);

  // ── Search state ────────────────────────────────────────────────────────────
  const [searchOpen,    setSearchOpen]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const scrollToBottom = useCallback((smooth = false) => {
    if (messagesRef.current) {
      messagesRef.current.scrollTo({ top: messagesRef.current.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    }
  }, []);

  // Track scroll position to show/hide scroll-to-bottom button
  const handleScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottom.current = distFromBottom < 80;
    setShowScrollBtn(distFromBottom > 200);
  };

  // Auto-scroll only when already at bottom
  useEffect(() => {
    if (isAtBottom.current) scrollToBottom();
  }, [channelMessages, typingUsers, scrollToBottom]);

  // Scroll to bottom on channel change
  useEffect(() => {
    isAtBottom.current = true;
    setShowScrollBtn(false);
    scrollToBottom();
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [currentChannelId, scrollToBottom]);

  // Ctrl+F — open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setSearchOpen(v => {
          if (!v) setTimeout(() => searchInputRef.current?.focus(), 50);
          return !v;
        });
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Search fetch — debounced
  useEffect(() => {
    if (!searchOpen || searchQuery.trim().length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const isDemoChannel = currentChannelId?.startsWith('demo-');
        if (isDemoChannel) {
          // Search local messages for demo mode
          const q = searchQuery.toLowerCase();
          const hits = channelMessages
            .filter(m => (m.text || m.content || '').toLowerCase().includes(q))
            .slice(-25).reverse();
          setSearchResults(hits);
        } else {
          const res = await fetch(
            `/api/servers/${currentServerId}/channels/${currentChannelId}/search?q=${encodeURIComponent(searchQuery)}&limit=25`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const data = await res.json();
          setSearchResults(data.results || []);
        }
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, searchOpen, currentChannelId, currentServerId, channelMessages]);

  const jumpToSearchResult = (msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('msg-highlight');
    setTimeout(() => el.classList.remove('msg-highlight'), 2000);
  };

  // Group messages: consecutive from same author within 5 min
  const processedMessages = channelMessages.map((msg, i) => {
    const prev = channelMessages[i - 1];
    const grouped = !!(prev &&
      (prev.senderName || prev.author) === (msg.senderName || msg.author) &&
      (prev.senderId   || prev.userId)  === (msg.senderId   || msg.userId) &&
      !msg.replyTo &&
      (msg.timestamp || msg.createdAt || 0) - (prev.timestamp || prev.createdAt || 0) < 300000
    );
    return { ...msg, grouped };
  });

  return (
    <div className="chat-area">
      {/* Channel Header */}
      <div className="chat-header">
        <button className="mobile-sidebar-toggle" onClick={onMobileOpenChannels} title="Channels" aria-label="Open channels">☰</button>
        <span className="channel-name-header">
          #{currentChannel?.name || 'general'}
        </span>
        {currentChannel?.topic && (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
            {currentChannel.topic}
          </span>
        )}
        {currentChannel?.slowmode > 0 && (
          <span title={`Slowmode: ${currentChannel.slowmode}s cooldown between messages`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#06b6d4', background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)', borderRadius: 6, padding: '2px 7px', marginLeft: 6, fontWeight: 600, flexShrink: 0, cursor: 'default' }}>
            🐌 {currentChannel.slowmode}s
          </span>
        )}
        <div className="chat-tools">
          <VoiceCallButton />
          <span onClick={() => onOpen('memberDirectory')}  className="chat-tool" style={{ cursor: 'pointer' }} title="Member Directory">👥</span>
          <span onClick={() => onOpen('bookmarks')}        className="chat-tool" style={{ cursor: 'pointer' }} title="Bookmarks">🔖</span>
          <span onClick={() => onOpen('notifications')}    className="chat-tool" style={{ cursor: 'pointer' }} title="Notifications">🔔</span>
          <span onClick={() => onOpen('pinnedMessages')}   className="chat-tool" style={{ cursor: 'pointer' }} title="Pinned Messages">📌</span>
          <span onClick={() => onOpen('threads')}          className="chat-tool" style={{ cursor: 'pointer' }} title="Threads">💬</span>
          <span onClick={() => { setSearchOpen(v => !v); setTimeout(() => searchInputRef.current?.focus(), 50); }} className="chat-tool" style={{ cursor: 'pointer', color: searchOpen ? 'rgb(var(--aethex-primary))' : undefined }} title="Search messages (Ctrl+F)">🔎</span>
          <span onClick={() => onOpen('quickSwitcher')}    className="chat-tool" style={{ cursor: 'pointer' }} title="Quick Switcher (Ctrl+K)">🔍</span>
          <span onClick={() => onOpen('appearance')}       className="chat-tool" style={{ cursor: 'pointer' }} title="Appearance">🎨</span>
          <span onClick={() => onToggleActivity?.()}       className="chat-tool" style={{ cursor: 'pointer' }} title="Toggle Activity Panel">⚡</span>
          <span onClick={() => onOpenSettings?.()}         className="chat-tool" style={{ cursor: 'pointer' }} title="Settings">⚙️</span>
          <button className="mobile-sidebar-toggle" onClick={onMobileOpenMembers} title="Members" aria-label="Open members">👥</button>
        </div>
      </div>

      {/* Search panel */}
      {searchOpen && (
        <div className="search-panel">
          <div className="search-input-row">
            <span className="search-icon">🔎</span>
            <input
              ref={searchInputRef}
              className="search-input"
              placeholder="Search messages…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              aria-label="Search messages"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>✕</button>
            )}
            <button className="search-close" onClick={() => setSearchOpen(false)} aria-label="Close search">✕ Close</button>
          </div>
          {searchQuery.length >= 2 && (
            <div className="search-results">
              {searchLoading ? (
                <div className="search-state">Searching…</div>
              ) : searchResults.length === 0 ? (
                <div className="search-state">No messages found for "{searchQuery}"</div>
              ) : (
                searchResults.map(msg => {
                  const body = msg.text || msg.content || '';
                  const q    = searchQuery.toLowerCase();
                  const idx  = body.toLowerCase().indexOf(q);
                  const pre  = body.slice(Math.max(0, idx - 30), idx);
                  const hit  = body.slice(idx, idx + q.length);
                  const post = body.slice(idx + q.length, idx + q.length + 60);
                  return (
                    <div key={msg.id} className="search-result-item" onClick={() => jumpToSearchResult(msg.id)}>
                      <div className="search-result-author">{msg.senderName || msg.author || 'Unknown'}</div>
                      <div className="search-result-preview">
                        {pre}<mark className="search-highlight">{hit}</mark>{post}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Special channel type views */}
      {currentChannel?.type === 'announcement' ? (
        <AnnouncementView
          channel={currentChannel}
          messages={processedMessages}
          replyTo={replyTo}
          onReply={setReplyTo}
          onThread={(msg) => onOpen('thread', { message: msg, serverId: currentServerId, channelId: currentChannelId })}
          serverId={currentServerId}
        />
      ) : currentChannel?.type === 'forum' ? (
        <ForumView channel={currentChannel} serverId={currentServerId} />
      ) : currentChannel?.type === 'stage' ? (
        <StageView channel={currentChannel} />
      ) : (
        <>
          {/* Messages */}
          <div id="chat-messages" className="chat-messages" ref={messagesRef} onScroll={handleScroll} style={{ position: 'relative' }} role="log" aria-label="Messages" aria-live="polite">
            {isLoading ? (
              <MessageSkeleton count={7} />
            ) : channelMessages.length === 0 ? (
              <div className="empty-state">
                <p style={{ fontSize: 32, marginBottom: 8 }}>💬</p>
                <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>#{currentChannel?.name || 'general'}</p>
                <p style={{ fontSize: 13, opacity: 0.5 }}>This is the beginning of #{currentChannel?.name || 'general'}. Start the conversation!</p>
              </div>
            ) : (
              processedMessages.map(msg => (
                <Message
                  key={msg.id}
                  grouped={msg.grouped}
                  id={msg.id}
                  author={msg.senderName || msg.author || msg.username}
                  senderName={msg.senderName || msg.author}
                  senderId={msg.senderId || msg.userId}
                  text={msg.text || msg.content}
                  content={msg.text || msg.content}
                  time={msg.time}
                  timestamp={msg.timestamp || msg.createdAt}
                  edited={msg.edited}
                  isEdited={msg.isEdited || msg.edited}
                  avatar={msg.avatar}
                  avatarBg={msg.avatarBg}
                  badge={msg.badge}
                  type={msg.type}
                  label={msg.label}
                  reactions={msg.reactions}
                  replyTo={msg.replyTo}
                  onReply={setReplyTo}
                  onThread={(msg) => onOpen('thread', { message: msg, serverId: currentServerId, channelId: currentChannelId })}
                />
              ))
            )}

            {/* Typing Indicators */}
            {typingUsers.length > 0 && (
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span className="dot" />
                  <span className="dot" style={{ animationDelay: '0.15s' }} />
                  <span className="dot" style={{ animationDelay: '0.3s' }} />
                </div>
                <span>
                  {typingUsers.slice(0, 3).map(u => u.userName || u.username).join(', ')}{' '}
                  {typingUsers.length === 1 ? 'is' : 'are'} typing…
                </span>
              </div>
            )}
          </div>

          {/* Scroll-to-bottom button */}
          {showScrollBtn && (
            <button className="scroll-bottom-btn" onClick={() => scrollToBottom(true)} title="Jump to latest">
              ↓ Jump to present
            </button>
          )}

          {/* Message Input */}
          <MessageInput replyTo={replyTo} onCancelReply={() => setReplyTo(null)} />
        </>
      )}
    </div>
  );
}
